import React, {useState, useRef, useEffect, useCallback, useMemo} from 'react';
import {
  PrimaryButton,
  SkeletonLoader,
  DropdownIndicator,
  customStyles,
} from 'components';
import {useAMLContext} from 'helpers/context/AMLContext';
import moment from 'moment';
import Select from 'react-select';
import {useQueryClient, useQuery, useMutation} from 'react-query';
import {toast} from 'react-toastify';
import {
  addComment,
  deleteComment,
  fetchComments,
} from 'requests/queries/customer360';
import {
  updateAssignee,
  updateMatchStatus,
  updateRiskLevel,
} from 'requests/queries/aml';
import {useSelector} from 'react-redux';
import InfoCard from './components/InfoCard';
import StatusDropdown from './components/StatusDropdown';
import CommentsSection from './components/CommentSection';
import {AMLReportDocument} from './components/generateAMLStructure';
import {dojahLogo} from '../../assets/images/images';

const AMLSidebar = ({isSearchLoading, members}) => {
  const {searchDetails} = useAMLContext();
  const entity = useMemo(() => searchDetails?.entity || {}, [searchDetails]);
  const {user} = useSelector(state => state?.auth);
  const queryClient = useQueryClient();

  const userName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`.trim()
      : 'Unknown User';

  const [matchStatus, setMatchStatus] = useState('No match');
  const [riskLevel, setRiskLevel] = useState('Low level');
  const [assignedTo, setAssignedTo] = useState(null);
  const [showMatchStatusDropdown, setShowMatchStatusDropdown] = useState(false);
  const [showRiskLevelDropdown, setShowRiskLevelDropdown] = useState(false);
  const [comment, setComment] = useState('');
  const [commentId, setCommentId] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [next, setNext] = useState(3);

  const matchStatusRef = useRef(null);
  const riskLevelRef = useRef(null);

  const matchStatusOptions = useMemo(
    () => [
      {value: 'No Match', color: '#F6F7F9', textColor: '#344272'},
      {value: 'Partial Match', color: '#D9E5F8', textColor: '#2A5392'},
      {value: 'Potential Match', color: '#E2F5F6', textColor: '#4A878D'},
      {value: 'Confirmed Match', color: '#FCDFDF', textColor: '#A13E3F'},
    ],
    [],
  );

  const riskLevelOptions = useMemo(
    () => [
      {
        value: 'Low level',
        label: 'Low',
        color: '#F0F6EF',
        textColor: '#55A249',
      },
      {
        value: 'Medium level',
        label: 'Medium',
        color: '#FFF8EB',
        textColor: '#DC6803',
      },
      {
        value: 'High level',
        label: 'High',
        color: '#FCDFDF',
        textColor: '#A13E3F',
      },
    ],
    [],
  );

  const assignToOptions = useMemo(
    () =>
      members?.team?.map(member => {
        const {first_name, last_name, email} = member?.user || {};
        const label =
          first_name && last_name ? `${first_name} ${last_name}` : email;
        return {
          value: member?.user?._id,
          label,
          color: '#F6F7F9',
          textColor: '#344272',
        };
      }) || [],
    [members],
  );

  useEffect(() => {
    if (!searchDetails) {
      setMatchStatus('No match');
      setRiskLevel('Low level');
      setAssignedTo(null);
      return;
    }

    setMatchStatus(entity.match_status || 'No match');
    setRiskLevel(
      entity.risk_level
        ? entity.risk_level === 'High'
          ? 'High level'
          : entity.risk_level === 'Medium'
          ? 'Medium level'
          : 'Low level'
        : 'Low level',
    );

    const assigneeOption = assignToOptions.find(
      option =>
        option.label.toLowerCase() === entity.assigned_to?.toLowerCase(),
    );
    setAssignedTo(
      assigneeOption || {value: 'N/A', label: entity.assigned_to || 'N/A'},
    );
  }, [searchDetails, entity, assignToOptions]);

  const {data: comments, isLoading: commentsLoading} = useQuery(
    ['aml-comments', entity.search_id],
    () => fetchComments(entity.search_id),
    {enabled: !!entity.search_id},
  );

  const {isLoading: addCommentLoading, mutate: addUserComment} = useMutation(
    data => addComment(entity.search_id, data),
    {
      onSuccess: () => {
        toast.success('Comment added successfully');
        queryClient.invalidateQueries(['aml-comments', entity.search_id]);
        setComment('');
      },
      onError: () => toast.error('Failed to add comment'),
    },
  );

  const {isLoading: deleteCommentLoading, mutate: deleteUserComment} =
    useMutation(commentId => deleteComment(entity.search_id, commentId), {
      onSuccess: () => {
        toast.success('Comment deleted successfully');
        queryClient.invalidateQueries(['aml-comments', entity.search_id]);
        setCommentId('');
      },
      onError: () => toast.error('Failed to delete comment'),
    });

  const {mutate: updateMatchStatusMutation} = useMutation(updateMatchStatus, {
    onSuccess: () => {
      toast.success('Match status updated successfully');
      queryClient.invalidateQueries(['searchDetails', entity.search_id]);
    },
    onError: () => toast.error('Failed to update match status'),
  });

  const {mutate: updateRiskLevelMutation} = useMutation(updateRiskLevel, {
    onSuccess: () => {
      toast.success('Risk level updated successfully');
      queryClient.invalidateQueries(['searchDetails', entity.search_id]);
    },
    onError: () => toast.error('Failed to update risk level'),
  });

  const {mutate: updateAssigneeMutation} = useMutation(updateAssignee, {
    onSuccess: () => {
      toast.success('Assignee updated successfully');
      queryClient.invalidateQueries(['searchDetails', entity.search_id]);
    },
    onError: () => toast.error('Failed to update assignee'),
  });

  const handleMatchStatusSelect = useCallback(
    option => {
      setMatchStatus(option.value);
      setShowMatchStatusDropdown(false);
      updateMatchStatusMutation({
        id: entity.id,
        matchStatus: option.value,
        userName,
      });
    },
    [entity.id, userName, updateMatchStatusMutation],
  );

  const handleRiskLevelSelect = useCallback(
    option => {
      setRiskLevel(option.value);
      setShowRiskLevelDropdown(false);
      updateRiskLevelMutation({
        id: entity.id,
        riskLevel: option.label,
        userName,
      });
    },
    [entity.id, userName, updateRiskLevelMutation],
  );

  const handleAssignToSelect = useCallback(
    option => {
      setAssignedTo(option);
      updateAssigneeMutation({
        id: entity.id,
        assignedTo: option?.label || null,
        userName,
      });
    },
    [entity.id, userName, updateAssigneeMutation],
  );

  const handleLoadMore = useCallback(() => setNext(prev => prev + 3), []);

  const handleDownloadReport = async () => {
    if (downloading) return;

    if (!entity || !entity.search_id) {
      toast.error('No data available to generate report');
      return;
    }

    setDownloading(true);
    toast.success('Report Downloaded Successfully!');

    try {
      let logoBase64 = dojahLogo;
      if (dojahLogo.startsWith('http') || dojahLogo.startsWith('/')) {
        try {
          const response = await fetch(dojahLogo);
          const blob = await response.blob();
          logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          toast.error('Failed to load logo image');
          console.error('Image Conversion Error:', error);
          return;
        }
      }

      const pdfData = [entity];
      const fileName = `aml_report_${entity.search_id}`;

      const {pdf} = await import('@react-pdf/renderer');
      const blob = await pdf(
        <AMLReportDocument
          data={pdfData}
          userName={userName}
          logoImage={logoBase64}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.pdf`.toLowerCase();
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      toast.error(`PDF generation failed: ${error.message}`);
      console.error('PDF Generation Error:', error);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        matchStatusRef.current &&
        !matchStatusRef.current.contains(event.target)
      ) {
        setShowMatchStatusDropdown(false);
      }
      if (
        riskLevelRef.current &&
        !riskLevelRef.current.contains(event.target)
      ) {
        setShowRiskLevelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex items-center justify-center w-full lg:w-[33%] flex-col gap-4">
      <div className="flex items-center w-full justify-between">
        {isSearchLoading ? (
          <SkeletonLoader style={{width: 80, height: 20, marginBottom: 10}} />
        ) : (
          <span
            className={`py-1 px-2 rounded-full font-medium text-white text-xs ${
              entity.schema_type?.toLowerCase() === 'organization'
                ? 'bg-[#17A6A0]'
                : 'bg-[#0B1C56]'
            }`}
          >
            {entity.schema_type?.toLowerCase() === 'organization'
              ? 'Organization'
              : 'Individual'}
          </span>
        )}
        {isSearchLoading ? (
          <SkeletonLoader style={{width: 100, height: 30, marginBottom: 10}} />
        ) : (
          <PrimaryButton
            buttonText={downloading ? 'Generating...' : 'Download Report'}
            className="!text-[10px]"
            onClick={handleDownloadReport}
            loading={downloading}
          />
        )}
      </div>

      <InfoCard
        title={entity.search_query || 'N/A'}
        value={`CASE ID: ${entity.search_id || 'N/A'}`}
        isLoading={isSearchLoading}
      />

      <div className="w-full grid grid-cols-2 gap-4">
        <InfoCard
          title="Date & time created"
          value={
            entity.createdAt
              ? moment(entity.createdAt).format('Do, MMMM YYYY, h:mma')
              : 'N/A'
          }
          isLoading={isSearchLoading}
        />
        <InfoCard
          title="Created by"
          value={entity.created_by || 'N/A'}
          isLoading={isSearchLoading}
        />
      </div>

      <div className="w-full grid grid-cols-2 gap-4">
        <InfoCard
          title="Gender"
          value={entity.raw_request?.properties?.gender || 'N/A'}
          isLoading={isSearchLoading}
        />
        <InfoCard
          title="Date of Birth"
          value={
            entity.raw_request?.properties?.date_of_birth
              ? moment(entity.raw_request.properties.date_of_birth).format(
                  'Do, MMMM YYYY',
                )
              : 'N/A'
          }
          isLoading={isSearchLoading}
        />
      </div>

      <div className="w-full grid grid-cols-2 gap-4">
        <InfoCard
          title="Match score"
          value={
            entity.raw_request?.screening_options?.match_threshold * 100 ||
            'N/A'
          }
          isLoading={isSearchLoading}
        />
        <InfoCard
          title="Ongoing monitoring"
          value={entity.monitoring ? 'Enabled' : 'Disabled'}
          isLoading={isSearchLoading}
        />
      </div>

      <StatusDropdown
        label="Match status"
        value={matchStatus}
        options={matchStatusOptions}
        onSelect={handleMatchStatusSelect}
        isLoading={isSearchLoading}
        dropdownRef={matchStatusRef}
        isOpen={showMatchStatusDropdown}
        setIsOpen={setShowMatchStatusDropdown}
      />

      <StatusDropdown
        label="Risk level"
        value={riskLevel}
        options={riskLevelOptions}
        onSelect={handleRiskLevelSelect}
        isLoading={isSearchLoading}
        dropdownRef={riskLevelRef}
        isOpen={showRiskLevelDropdown}
        setIsOpen={setShowRiskLevelDropdown}
      />

      <div className="bg-white rounded p-4 flex items-start gap-1 w-full flex-col">
        <p className="text-[#080917] text-[10px] font-normal uppercase">
          Assign case to
        </p>
        {isSearchLoading ? (
          <SkeletonLoader style={{width: 150, height: 24, marginBottom: 10}} />
        ) : (
          <Select
            value={assignedTo}
            placeholder="Select assignee..."
            components={{DropdownIndicator}}
            classNamePrefix="react-select"
            styles={customStyles}
            options={[{value: 'N/A', label: 'N/A'}, ...assignToOptions]}
            onChange={handleAssignToSelect}
            isClearable
            className="!w-full"
          />
        )}
      </div>

      <CommentsSection
        comments={comments}
        isLoading={commentsLoading}
        addCommentLoading={addCommentLoading}
        deleteCommentLoading={deleteCommentLoading}
        addUserComment={addUserComment}
        deleteUserComment={deleteUserComment}
        comment={comment}
        setComment={setComment}
        commentId={commentId}
        setCommentId={setCommentId}
        next={next}
        handleLoadMore={handleLoadMore}
      />
    </div>
  );
};

export default AMLSidebar;
